import {
  DATADOG_SERVICE,
  DATADOG_SESSION_SAMPLE_RATE,
  DATADOG_TRACE_SAMPLE_RATE,
  DATADOG_SESSION_REPLAY_SAMPLE_RATE,
  DATADOG_ENV,
  DATADOG_VERSION
} from '../env/public';
import config from '../config';

/**
 * Enumerates propagator types supported by RUM to connect resources with
 * backends that are instrumented with OpenTelemetry libraries.
 *
 * @see https://docs.datadoghq.com/tracing/other_telemetry/rum/?tab=browserrum#how-are-rum-resources-linked-to-traces
 */
export enum PropagatorType {
  /**
   * Datadog's native propagator headers are used to propagate trace information
   * specific to Datadog's tracing system.
   */
  DATADOG = 'datadog',

  /**
   * B3 single header propagator combines multiple pieces of trace information
   * into a single header.
   */
  B3 = 'b3',

  /**
   * B3 multi-header propagator splits the trace information into multiple
   * headers.
   */
  B3MULTI = 'b3multi',

  /**
   * The W3C Trace Context specification, which is designed to be a
   * cross-platform, cross-vendor standard for trace context propagation.
   */
  TRACECONTEXT = 'tracecontext'
}

const datadog = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /** Indicates whether or not the datadog monitoring is active. */
      enabled: true,
      /** The client token. */
      clientToken: undefined as unknown as string, // Must be overridden by the site.
      /** The application ID. */
      applicationId: undefined as unknown as string, // Must be overridden by the site.
      /** The session sample rate. */
      sessionSampleRate: Number(DATADOG_SESSION_SAMPLE_RATE ?? 100),
      /** The session replay sample rate. */
      sessionReplaySampleRate: Number(
        DATADOG_SESSION_REPLAY_SAMPLE_RATE ?? 100
      ),
      /**
       * The percentage of requests to trace: 100 for all, 0 for none.
       * @see https://docs.datadoghq.com/tracing/other_telemetry/rum/?tab=browserrum#how-are-rum-resources-linked-to-traces
       */
      traceSampleRate:
        DATADOG_TRACE_SAMPLE_RATE != null
          ? Number(DATADOG_TRACE_SAMPLE_RATE)
          : undefined,
      /**
       * The environment where the log should occur.
       * Available values are: feature, dev, uat/staging, prod.
       */
      env: {
        dev: DATADOG_ENV ?? 'dev',
        qa: DATADOG_ENV ?? 'qa',
        uat: DATADOG_ENV ?? 'uat/staging',
        prod: DATADOG_ENV ?? 'prod'
      },
      /** The team that is running datadog monitoring. */
      team: 'frontend',
      /** The the service where datadog is running. */
      service: DATADOG_SERVICE,
      /**
       * Indicates whether to track user interactions.
       * From v5+, this option also enables frustration signal tracking.
       *
       * @see https://docs.datadoghq.com/real_user_monitoring/browser/frustration_signals/
       */
      trackUserInteractions: true,
      /** Indicates whether to track resources. */
      trackResources: true,
      /** Indicates whether to track long tasks. */
      trackLongTasks: true,
      /** The default privacy level. */
      defaultPrivacyLevel: 'mask-user-input',
      /** Indicates whether to silently handle multiple initialization calls. */
      silentMultipleInit: true,
      /** Indicates whether to use secure session cookie. */
      useSecureSessionCookie: true,
      /** The version of the object. */
      version: DATADOG_VERSION ?? '1.0.0',
      /**
       * An array of {@link PropagatorType} to propagate trace context across
       * different services.
       */
      propagatorTypes: [PropagatorType.TRACECONTEXT] as Array<PropagatorType>,
      /**
       * Request origins ignored when computing the page activity.
       * Excludes third-party analytics, tracking, and marketing domains from
       * affecting page activity calculations.
       *
       * @see https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#how-page-activity-is-calculated
       */
      excludedActivityUrls:
        'https?:\\/\\/(.*\\.)?' +
        '(' +
        // Google Analytics & Ads
        'googleads\\.g\\.doubleclick\\.net|analytics\\.google\\.com|googletagmanager\\.com|google-analytics\\.com|googleadservices\\.com|doubleclick\\.net|' +
        // Social Media & Content
        'stylitics\\.com|reddit\\.com|redditstatic\\.com|pinterest\\.com|facebook\\.net|facebook\\.com|spotify\\.com|' +
        // Analytics & Personalization Services
        'afterpay\\.com|dynamicyield\\.com|fullstory\\.com|tealiumiq\\.com|contentsquare\\.net|rakuten\\.com|' +
        // TikTok Analytics
        'tiktok\\.com|tiktokw\\.us|' +
        // Customer Support & Experience
        'gladly\\.com|locally\\.com|pixlee\\.co|' +
        // Marketing & Advertising Platforms
        'forter\\.com|yottaa\\.com|yottaa\\.net|talon\\.one|' +
        // Privacy & Consent Management
        'osano\\.com|' +
        // Customer Feedback & Surveys
        'medallia\\.eu|kampyle\\.com|' +
        // Payment Services
        'paypal\\.com|paypalobjects\\.com|venmo\\.com|apple\\.com|chasepaymentech\\.com|' +
        // CDNs & External Assets
        'cloudflare\\.com|unpkg\\.com|cloudfront\\.net|' +
        // Bot Protection & Security
        'datadome\\.co|' +
        // Content Management & Builder Tools
        'builder\\.io|lightboxcdn\\.com|' +
        // Reviews & E-commerce Services
        'yotpo\\.com|pitneybowes\\.com|' +
        // Student Discount Services
        'myunidays\\.com|' +
        // AWS Services
        'amazonaws\\.com|' +
        // External Image & Asset Hosting
        'cloudinary\\.com|salesforce\\.com|' +
        // Datadog Self-Exclusion (prevent recursive tracking)
        'datadoghq\\.com' +
        ')' +
        '($|\\/.*)'

      // NOTE: `allowedTracingUrlsMatch` is now determined dynamically in the
      // `Datadog` component based on the current deployment URL, eliminating
      // the need for hardcoded brand-specific patterns.
    }
  },

  'SANUK-US': {
    default: {
      applicationId: 'b2b1532f-a0b7-4641-8cd6-96fc7ea3addd',
      clientToken: 'pub02a80a9b7eb0518c38a1639d05cdcc26',
      service: DATADOG_SERVICE ?? 'sanuk'
    }
  },
  'AHNU-US': {
    default: {
      applicationId: '8a765075-fb5d-4b41-8bc7-fe91b0fd8659',
      clientToken: 'pub460860eb940a7ad13a712380165c2f7d',
      service: DATADOG_SERVICE ?? 'ahnu'
    }
  },
  'TEVA-US': {
    default: {
      applicationId: '056a366a-5ecd-4052-bbb4-9c8d896eca86',
      clientToken: 'pub516859aa96d2198023aafb37378328aa',
      service: DATADOG_SERVICE ?? 'teva'
    }
  },
  'UGG-COLLAB-US': {
    default: {
      // TODO: Update the applicationId and clientToken with the correct values for UGG Collab. (These are the same as Ahnu.)
      applicationId: '8a765075-fb5d-4b41-8bc7-fe91b0fd8659',
      clientToken: 'pub460860eb940a7ad13a712380165c2f7d',
      service: DATADOG_SERVICE ?? 'ugg-collab'
    }
  },
  'UGG-US': {
    default: {
      applicationId: '9a736db2-c55a-49f0-9980-30d9a9160808',
      clientToken: 'pubdc2dcdd7515c478a47cda3fa3a1a87fe',
      service: process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? 'ugg'
    }
  },
  'KOOLABURRA-US': {
    default: {
      // TODO: Update the applicationId and clientToken with the correct values for Teva. (These are the same as Ahnu.)
      applicationId: '8a765075-fb5d-4b41-8bc7-fe91b0fd8659',
      clientToken: 'pub460860eb940a7ad13a712380165c2f7d',
      service: process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? 'teva'
    }
  },
  'HOKA-US': {
    default: {
      applicationId: '33b49d7b-50e7-4d49-b3e2-8f32f79ad731',
      clientToken: 'pubf86c9081e9e37cfd256c0b2c6e890c80',
      service: process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? 'hoka'
    }
  }
});

export default datadog;
